// src/components/MarqueeStrip.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Sparkles } from 'lucide-react';

const MarqueeStrip = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'marquee');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching marquee:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();

    // Listen for updates from admin panel
    const handleUpdate = () => fetchConfig();
    window.addEventListener('marqueeUpdated', handleUpdate);
    return () => window.removeEventListener('marqueeUpdated', handleUpdate);
  }, []);

  if (loading || !config || config.active === false) return null;
  if (!config.text || config.text.trim() === '') return null;

  // Support multiple messages separated by "|"
  const messages = config.text.split('|').map(t => t.trim()).filter(Boolean);
  if (messages.length === 0) return null;

  const speed = config.speed || 'normal';
  const durationMap = { slow: 40, normal: 25, fast: 15 };
  const duration = durationMap[speed] || 25;

  const bgColor = config.bgColor || '#16a34a';
  const textColor = config.textColor || '#ffffff';

  // Build the text item with separator
  const TextItem = ({ text, withIcon = true }) => (
    <div className="flex items-center gap-3 md:gap-4 px-4 md:px-6 shrink-0">
      {withIcon && (
        <Sparkles
          size={14}
          className="shrink-0 md:w-4 md:h-4"
          style={{ color: config.iconColor || '#facc15' }}
          fill={config.iconColor || '#facc15'}
        />
      )}
      <span className="text-xs md:text-sm font-semibold tracking-wide whitespace-nowrap">
        {text}
      </span>
      <span
        className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full shrink-0"
        style={{ backgroundColor: config.iconColor || '#facc15' }}
      />
    </div>
  );

  // For the marquee, duplicate the content twice so it's seamless
  const fullContent = [...messages, ...messages, ...messages];

  return (
    <div
      className="relative w-full overflow-hidden py-2.5 md:py-3 shadow-md"
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      {/* Left fade */}
      <div
        className="absolute left-0 top-0 bottom-0 w-8 md:w-16 z-10 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, ${bgColor} 0%, transparent 100%)`,
        }}
      ></div>

      {/* Right fade */}
      <div
        className="absolute right-0 top-0 bottom-0 w-8 md:w-16 z-10 pointer-events-none"
        style={{
          background: `linear-gradient(270deg, ${bgColor} 0%, transparent 100%)`,
        }}
      ></div>

      {/* Scrolling track */}
      <div
        className="flex items-center"
        style={{
          animation: `marquee-scroll ${duration}s linear infinite`,
          width: 'fit-content',
        }}
      >
        {fullContent.map((text, i) => (
          <TextItem key={i} text={text} />
        ))}
      </div>

      {/* Inline styles for animation */}
      <style>{`
        @keyframes marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-scroll { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default MarqueeStrip;